/**
 * S? Settings — the account, and signing out. 2026-08-06.
 *
 * ## 🔴 Why this exists now, and it is not because settings arrived
 *
 * Nothing configurable has moved out of YAML: everything the product reads is a file loaded at
 * service start, and exposing that to a form means deciding what a live change is allowed to do
 * mid-trade. That is unchanged, and the screen says so.
 *
 * What forced the screen is that **there was no way to sign out on a phone**. Sign-out lived in
 * the annunciator rail's tail, and the rail drops its account block below 720px — the five
 * machine-state fields are what the rail is for, and the account is the one thing in it that is
 * not machine state. Correct for the rail, and it left the operator with no exit.
 *
 * ⚠ **Settings is where an account action belongs anyway**, on every desktop platform and every
 * phone one. Putting it here rather than restoring it to the phone rail also means there is one
 * place to sign out rather than two that can disagree about whether the session ended.
 *
 * The rail keeps its desktop copy: it is one click from anywhere, and an operator who wants out
 * during an incident should not have to navigate first.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type JSX, useState } from 'react';

import { useAuth } from '../auth/AuthProvider';
import { api } from '../api/client';
import { Button } from '../design/components/controls/Button.jsx';
import { ErrorPanel } from '../design/components/messages/ErrorPanel.jsx';
import { Skeleton } from '../design/components/messages/Skeleton.jsx';
import { type FleetView, readFleetView, writeFleetView } from '../ui/preferences';
import { clockOf, MODE_LABELS,
  type TimestampMode, resolvedZone, setTimestampMode, useTimestampMode } from '../ui/timestamps';
import { PLACEHOLDER_COPY } from './Placeholder';
import { deviceOf, orderSessions } from './sessions';

/**
 * The Fleet screen's two views, named the same way it names them.
 *
 * ⚠ **The labels are `FleetScreen`'s verbatim.** A settings screen that called these "Grouped" and
 * "Flat" would be a second vocabulary for one thing, and the operator would have to work out which
 * control moved which segment.
 */
/** Its own key so a revoke invalidates exactly this list and nothing else. */
const SESSIONS_QUERY_KEY = ['auth', 'sessions'];

/** Absence has its own mark — `—` is not zero, and not a blank either. */
const NO_VALUE = '—';

const FLEET_VIEWS: ReadonlyArray<{ value: FleetView; label: string; title: string }> = [
  {
    value: 'families',
    label: 'Families',
    title: "§8's reading: one row per family, expanding to its swept dimensions",
  },
  {
    value: 'rows',
    label: 'All rows',
    title: 'Every candidate, with the nine per-candidate columns',
  },
];

/**
 * The two timestamp modes, labelled the way the header labels them.
 *
 * ⚠ **`MODE_LABELS` rather than strings typed again here.** The header pill and this control name
 * one thing, and a settings screen that called them "Coordinated Universal" and "Your time" would
 * be a second vocabulary the operator has to map onto the first.
 */
const TIMESTAMP_MODES: ReadonlyArray<{ value: TimestampMode; label: string; title: string }> = [
  {
    value: 'utc',
    label: MODE_LABELS.utc,
    title: 'What the system records in. The default, and the one that cannot be misread.',
  },
  {
    value: 'local',
    label: MODE_LABELS.local,
    title: `This browser's own timezone${zoneNote()}`,
  },
];

/** ` — Europe/Amsterdam`, or a full stop where `Intl` cannot say. */
function zoneNote(): string {
  const zone = resolvedZone();
  return zone ? ` — ${zone}.` : '.';
}

export function SettingsScreen(): JSX.Element {
  const { account, signOut } = useAuth();
  const [fleetView, setFleetView] = useState<FleetView>(() => readFleetView());
  // From the store, not local state: the header toggle writes the same value, and a copy
  // here would leave this screen showing a stale mode after the header changed it.
  const timestampMode = useTimestampMode();

  // Written on change rather than behind a Save. There is one setting, it takes effect the next
  // time Fleet opens, and it is reversible in one click — a Save button would add a state in which
  // the screen shows one thing and the browser remembers another.
  const chooseFleetView = (value: FleetView) => {
    setFleetView(value);
    writeFleetView(value);
  };

  return (
    <div className="jmt-screen jmt-screen--capped">
      <section className="jmt-panel">
        <h2 className="jmt-section-heading">Account</h2>
        <div className="jmt-account">
          <div className="jmt-account__who">
            <span className="jmt-account__email">{account?.email ?? '—'}</span>
            {/* ⚠ The *server* holds the session; this button ends it there. The UI has no
                authority over a session and must not imply it does — `signOut` calls the API and
                the redirect follows the answer, so a failed sign-out leaves the operator signed
                in and looking at the reason rather than at a login screen that is a lie. */}
            <span className="jmt-account__note">
              Signing out ends the session on the server, not just in this browser.
            </span>
          </div>
          {/* Two actions, one row. `Button.prompt.md` allows at most one `primary` per surface and
              this screen has none — signing out is not the most important thing you can do here.
              `ghost` for the wider one because it is rarer, not because it is safer. */}
          <div className="jmt-account__actions">
            <Button variant="secondary" onClick={() => void signOut()}>
              Sign out
            </Button>
            <Button
              variant="ghost"
              title="Ends every session for this account, including this one"
              onClick={() => void signOut(true)}
            >
              Sign out everywhere
            </Button>
          </div>
        </div>
        {/* 🔴 **Backlog item 8, and it belongs here rather than in a panel of its own.**
            `GET /auth/sessions` and `DELETE /auth/sessions/{id}` have existed since task 30 with
            nothing asking for them, so *"one action revokes all sessions everywhere"* — the reason
            `AuthN-AuthZ-and-User-Management.md` gives for sessions being rows rather than JWTs —
            could not be reached from any screen. These are facts *about the account*, so a second
            heading for them was one heading too many. */}
        <SessionList />
      </section>


      <section className="jmt-panel">
        <h2 className="jmt-section-heading">Fleet</h2>
        {/* One line. The first version explained §8's reading, what each view answers and when
            each is asked — four sentences above a two-button control whose own labels already say
            it, and the operator's verdict was that one line is enough. The `title` on each segment
            still carries the longer reason for anyone who wants it. */}
        <p className="jmt-note">Which view the Fleet screen opens on.</p>
        {/* The same segmented control the Fleet screen itself uses, so the choice here looks like
            the thing it changes. `jmt-views` is app-owned in `app.css`, not vendored. */}
        <div className="jmt-views" role="group" aria-label="Fleet default view">
          {FLEET_VIEWS.map((option) => (
            <button
              type="button"
              key={option.value}
              className="jmt-view"
              data-on={fleetView === option.value ? '' : undefined}
              aria-pressed={fleetView === option.value}
              title={option.title}
              onClick={() => chooseFleetView(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

      </section>

      {/* 🔴 **`UI-Design-Spec.md` line 1059 puts the timestamp default under Appearance, and on a
          phone this is the ONLY way to reach it.** `.jmt-top__tz` is `display: none` below 720px —
          correct, the rail drops it for width alongside the mode pill — so the header toggle is the
          desktop affordance and this is the phone's.

          ⚠ **Two controls, one store.** Both read and write `ui/timestamps`, so they cannot come to
          disagree about the mode — the same argument this screen's own header makes about having one
          place to sign out rather than two. */}
      <section className="jmt-panel">
        <h2 className="jmt-section-heading">Appearance</h2>
        <p className="jmt-note">
          Which timezone every timestamp is shown in. UTC is the default because a trade log read in
          the wrong zone is a wrong trade log, and the active mode stays visible in the header.
          Stored times do not change.
        </p>
        <div className="jmt-views" role="group" aria-label="Timestamp timezone">
          {TIMESTAMP_MODES.map((option) => (
            <button
              type="button"
              key={option.value}
              className="jmt-view"
              data-on={timestampMode === option.value ? '' : undefined}
              aria-pressed={timestampMode === option.value}
              title={option.title}
              onClick={() => setTimestampMode(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </section>

      <section className="jmt-panel">
        <h2 className="jmt-section-heading">Configuration</h2>
        <p className="jmt-note">{PLACEHOLDER_COPY.settings}</p>
      </section>
    </div>
  );
}

/**
 * The account's active sessions, each revocable except the one reading the screen.
 *
 * 🔴 **The current row carries a label instead of a button, and that is a correctness choice.**
 * `DELETE /auth/sessions/{id}` revokes the row and does **not** clear the cookie — only
 * `/auth/logout` does — so revoking your own session from here would leave the browser holding a
 * token that 401s on its next request. The UI would look signed in until something asked. Ending
 * *this* session is what the Sign out button above already does, correctly.
 *
 * ⚠ **`danger` is not used**, on any button here. `Button.prompt.md`: *"`danger` is the single
 * filled-red button in the product"* and it belongs to the kill switch. Revoking a session is
 * consequential and recoverable — you sign in again — so it is `secondary` and it carries a word
 * rather than an icon.
 */
function SessionList(): JSX.Element {
  const queryClient = useQueryClient();
  const timestampMode = useTimestampMode();
  const [busy, setBusy] = useState<string | null>(null);
  // 🔴 **A failed revoke used to be silent.** The promise rejected, the console carried an
  // `Uncaught (in promise)`, and the screen showed nothing at all — so the operator presses Revoke,
  // the row stays, and the only available reading is that the button does not work. Found by
  // hitting a real 403 (a CSRF token this dev session did not carry) while verifying the happy
  // path, which is the one failure mode a happy-path check cannot show.
  const [failed, setFailed] = useState<string | null>(null);

  const sessions = useQuery({
    queryKey: SESSIONS_QUERY_KEY,
    queryFn: () => api.auth.listSessions(),
  });

  const revoke = async (id: string) => {
    setBusy(id);
    setFailed(null);
    try {
      await api.auth.revokeSession({ sessionId: id });
      await queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY });
    } catch (error) {
      // Plain language, and it says what is still true: the session was NOT ended. A message that
      // only reported the failure would leave the operator unsure whether it half-happened.
      setFailed(
        `That session is still active — the server refused the request. ${
          (error as { status?: number })?.status === 404
            ? 'It may already have ended.'
            : 'Nothing has changed.'
        }`,
      );
    } finally {
      // Cleared in `finally`: a failed revoke must not leave the row disabled forever, which would
      // be a session the operator can no longer end from the one screen that can end it.
      setBusy(null);
    }
  };

  if (sessions.isPending) return <Skeleton count={3} />;
  if (sessions.isError) {
    return (
      <ErrorPanel
        message="Could not read the session list."
        detail={String((sessions.error as Error)?.message ?? '')}
        onRetry={() => void sessions.refetch()}
      />
    );
  }

  const rows = orderSessions(sessions.data.sessions);

  return (
    <>
      {/* `role="status"` so a screen reader is told without stealing focus — the operator is still
          on the button they pressed. */}
      {failed ? (
        <p className="jmt-note jmt-sessions__failed" role="status">
          {failed}
        </p>
      ) : null}
      <ul className="jmt-sessions">
        {rows.map((row) => {
          const device = deviceOf(row.user_agent);
          return (
            <li className="jmt-sessions__row" key={row.id}>
              <span className="jmt-sessions__what" title={row.user_agent ?? undefined}>
                {device ?? NO_VALUE}
                {row.current ? <span className="jmt-sessions__here"> · this device</span> : null}
              </span>
              {/* `—` for an absent address rather than a blank: a session with none recorded is a
                  different fact from one at 0.0.0.0. */}
              <span className="jmt-sessions__when">
                {row.ip ?? NO_VALUE} · {clockOf(row.last_seen_at, timestampMode)}
              </span>
              {row.current ? (
                <span className="jmt-sessions__pad" />
              ) : (
                <Button
                  variant="ghost"
                  disabled={busy !== null}
                  onClick={() => void revoke(row.id)}
                >
                  {busy === row.id ? 'Revoking' : 'Revoke'}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}
